<template>
  <v-dialog
    v-model="show"
    persistent
    @keydown.esc="hide"
    width="500"
    v-if="role('admin', 'safetyRepresentative')"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-if="role('admin', 'safetyRepresentative')"
        v-on="on"
        :loading="$apollo.loading"
        class="primary accent-4 white--text mr-2"
      >
        <v-icon dark left>add</v-icon>
        {{ $t("extTrainings.category.change") }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="headline grey lighten-2" primary-title>
        {{ $t("extTrainings.category.change") }}
      </v-card-title>
      <v-card-text>
        <div>{{ $t("extTrainings.category.existingCategories") }}</div>
        <template>
          <v-chip
            v-for="et in extTrainingCategories"
            :key="et.id"
            class="mr-1 mb-1"
            small
            :close="canDelete(et.category)"
            close-icon="delete_forever"
            @click="editCategoryDetails(et)"
            @click:close="deleteCategory(et)"
            ><a style="text-decoration: underline;">{{ et.category }}</a>
            <v-icon v-if="role('safetyRepresentative')" small class="ml-2"
              >{{ et.isSecure ? "lock_outline" : "lock" }}
            </v-icon>
          </v-chip>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-text>
        <v-form ref="form">
          <v-text-field
            v-model="newCategory"
            class=""
            :label="
              isNewCategory
                ? $t('extTrainings.category.new')
                : $t('extTrainings.category.existing')
            "
            hide-details="auto"
            :rules="[rules.required]"
          />
          <v-text-field
            v-model="defaultDueInMonths"
            type="number"
            :label="$t('extTrainings.category.defaultDueInMonths')"
            hide-details="auto"
            :rules="[rules.required, rules.positiveNumber]"
            :disabled="noOverDue"
          />
          <v-checkbox
            v-model="noOverDue"
            :label="$t('extTrainings.category.noOverDue')"
            hide-details="auto"
            class="mt-1"
          />
          <v-checkbox
            :disabled="!role('safetyRepresentative')"
            v-model="isSecure"
            :label="$t('extTrainings.category.isSecure')"
            hide-details="auto"
          />
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="grey" text @click="hide">{{ $t("dialog.cancel") }}</v-btn>

        <v-btn color="primary" text @click="change" :loading="loading">{{
          isNewCategory ? $t("dialog.add") : $t("dialog.change")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import UpsertExtTrainingCategory from "@/graphql/mutation/extTraining/UpsertExtTrainingCategory.gql";
import DeleteExtTrainingCategory from "@/graphql/mutation/extTraining/DeleteExtTrainingCategory.gql";
import ExtTrainingsQuery from "@/graphql/queries/ExtTrainings.gql";

export default {
  props: ["extTrainingCategories", "extTrainingGroups"],
  data() {
    return {
      loading: false,
      show: false,
      newCategory: "",
      rules: {},
      defaultDueInMonths: 12,
      isSecure: false,
      noOverDue: false,
      id: "",
    };
  },
  mounted() {
    this.deactivateRules();
  },
  computed: {
    ...mapGetters(["role"]),

    isNewCategory() {
      if (this.extTrainingCategories && this.extTrainingCategories.length) {
        const categoryExists = this.extTrainingCategories.some(
          (e) => e.category === this.newCategory
        );
        return !categoryExists;
      }
      return true;
    },

    categoriesUsedInExtTrainings() {
      return (
        (this.extTrainings &&
          this.extTrainingsInactive &&
          this.extTrainings
            .concat(this.extTrainingsInactive)
            .map((e) => e.category)
            .filter((v, i, a) => a.indexOf(v) === i)) ||
        []
      );
    },
    categoriesUsedInExtTrainingsGroups() {
      return (
        (this.extTrainingGroups &&
          this.extTrainingGroups
            .map((g) => g.extTrainingCategories.map((etc) => etc.category))
            .flat()
            .filter((v, i, a) => a.indexOf(v) === i)) ||
        []
      );
    },
  },
  methods: {
    canDelete(category) {
      return (
        this.extTrainings &&
        this.extTrainingsInactive &&
        !this.categoriesUsedInExtTrainings.some((x) => x === category) &&
        this.extTrainingGroups &&
        !this.categoriesUsedInExtTrainingsGroups.some((x) => x === category)
      );
    },
    hide() {
      this.deactivateRules();
      this.$refs.form.resetValidation();
      this.newCategory = "";
      this.show = false;
    },
    change() {
      this.activateRules();
      this.$nextTick(() => {
        if (this.$refs.form.validate()) {
          this.loading = true;
          if (this.noOverDue) {
            this.defaultDueInMonths = 0;
          }
          const variables = {
            category: this.newCategory,
            defaultDueInMonths: this.defaultDueInMonths,
            isSecure: this.isSecure,
          };
          if (!this.isNewCategory) {
            variables.id = this.id;
          }
          this.$apollo
            .mutate({
              mutation: UpsertExtTrainingCategory,
              variables: variables,
            })
            .then(() => {
              this.$emit("refreshCategories");
              this.$store.commit("push_global_message", {
                color: "success",
                text: this.isNewCategory
                  ? `${this.$t("extTrainings.category.added", {
                      text: this.newCategory,
                    })}`
                  : `${this.$t("extTrainings.category.edited")}`,
              });
            })
            .catch((err) => {
              this.$store.commit("push_global_message", {
                color: "error",
                text: err,
              });
            })
            .finally(() => {
              this.hide();
              this.loading = false;
            });
        }
      });
    },
    editCategoryDetails(et) {
      this.newCategory = et.category;
      this.defaultDueInMonths = et.defaultDueInMonths;
      this.isSecure = et.isSecure;
      this.noOverDue = et.defaultDueInMonths === 0;
      this.id = et.id;
    },
    deleteCategory(et) {
      if (!et) {
        return;
      }
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: DeleteExtTrainingCategory,
          variables: {
            id: et.id,
          },
        })
        .then(() => {
          this.$emit("refreshCategories");
          this.$store.commit("push_global_message", {
            color: "warning",
            text: `${this.$t("extTrainings.category.deleted")} ${et.category}`,
          });
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deactivateRules() {
      this.rules = {
        required: () => true,
        exists: () => true,
        positiveNumber: () => true,
      };
    },
    activateRules() {
      this.rules = {
        required: () =>
          !!this.newCategory || this.$i18n.t("validation.required"),
        positiveNumber: () =>
          (this.defaultDueInMonths >= 0 &&
            parseInt(this.defaultDueInMonths) >= 0) ||
          this.$i18n.t("validation.positiveNumber"),
      };
    },
  },
  apollo: {
    extTrainings: {
      query: ExtTrainingsQuery,
      variables() {
        return {
          showActive: true,
        };
      },
      update(data) {
        return data.extTrainings;
      },
      skip() {
        return !this.show;
      },
    },
    extTrainingsInactive: {
      query: ExtTrainingsQuery,
      variables() {
        return {
          showActive: false,
        };
      },
      update(data) {
        return data.extTrainings;
      },
      skip() {
        return !this.show;
      },
    },
  },
};
</script>
